import React, {
  useState,
  useEffect,
  useCallback,
} from 'react';
import {
  shallowEqual,
  useSelector,
  useDispatch,
} from 'react-redux';
import {
  getStatus,
} from 'redux-resource';
import i18n from 'i18next';

import {
  coreActions,
  coreSelectors,
} from '@gymcloud/core';

import {
  Spinner,
  ProgramWorkoutHeader,
  WorkoutExercisesList,
} from 'web-components';

import {
  Flex,
  Text,
  WorkoutExercisesView,
} from 'web-styled';

import * as S from './styled';

type Props = {
  resourceType: string,
  programId: number,
};

const Workout = ({
  resourceType,
  programId,
}: Props) => {
  const dispatch = useDispatch();
  const workoutType = resourceType === 'programTemplates'
    ? 'workoutTemplates'
    : 'personalWorkouts';
  const mapState = useCallback(state => ({
    weeks: coreSelectors.getProgamWeeks(
      programId,
      resourceType,
    )(state),
    allWorkouts: coreSelectors.getResourceMappedList(
      'programWorkouts',
      `program_${programId}`,
    )(state),
    workoutReadStatus: getStatus(
      state,
      [
        `${workoutType}.requests.getFullWorkout.status`,
      ],
      true,
    ),
  }), [programId]);

  const {
    weeks,
    allWorkouts,
    workoutReadStatus,
  } = useSelector(mapState, shallowEqual);

  const [activeTabs, setTab] = useState({});

  useEffect(() => {
    if (programId) {
      allWorkouts.forEach(pw => {
        dispatch(
          coreActions.requestFullWorkout({
            id: pw.workout_id,
            resourceType: workoutType,
          }),
        );
      });
    }
  }, [programId]);

  if (workoutReadStatus.pending) {
    return (
      <Flex width="100%" height="200px">
        <Spinner message="Loading..." />
      </Flex>
    );
  }

  return (
    weeks.length
      ? (
        <S.WorkoutWrapper>
          {weeks.map(w => (
            <React.Fragment key={w.id}>
              <Text
                size="20px"
                color="#333"
              >
                {w.name}
              </Text>
              {w.workouts.length ? w.workouts.map(workout => (
                <WorkoutExercisesView key={workout.id}>
                  <ProgramWorkoutHeader
                    readOnly
                    activeTab={activeTabs[workout.id] || 'description'}
                    resourceType={resourceType === 'programTemplates'
                      ? 'workoutTemplates'
                      : 'personalWorkouts'}
                    programWorkout={workout}
                    setTab={(tab) => {
                      setTab({
                        ...activeTabs,
                        [workout.id]: tab,
                      });
                    }}
                  />
                  {(activeTabs[workout.id] !== 'warmup'
                      && activeTabs[workout.id] !== 'cooldown' && (
                      <WorkoutExercisesList
                        type="workout"
                        readOnly
                        workoutId={workout.workout_id}
                      />
                  ))}
                </WorkoutExercisesView>
              )) : (
                <Flex width="100%" justify="center">
                  <S.Empty>
                    {i18n.t('program.week.empty')}
                  </S.Empty>
                </Flex>
              )}
            </React.Fragment>
          ))}
        </S.WorkoutWrapper>
      ) : (
        <Flex fillSpace align="center" justify="center">
          <S.Empty>
            {i18n.t('program.empty')}
          </S.Empty>
        </Flex>
      )
  );
};

export default Workout;
